import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  Container,
  Stepper,
  Step,
  StepLabel,
  Card,
  CardContent,
  Chip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { authService } from '../../services/authService';
import { profileLinkService } from '../../services/profileLinkService';
import { MemberProfile } from '../../types/auth';

type ClaimStep = 'search' | 'verify' | 'pending';

export const ProfileClaiming: React.FC = () => {
  const [step, setStep] = useState<ClaimStep>('search');
  const [searchQuery, setSearchQuery] = useState('');
  const [memberNumber, setMemberNumber] = useState('');
  const [verificationEmail, setVerificationEmail] = useState('');
  const [foundProfiles, setFoundProfiles] = useState<MemberProfile[]>([]);
  const [selectedProfile, setSelectedProfile] = useState<MemberProfile | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const { user, updateProfile, skipProfileClaim } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const steps = ['Find Profile', 'Verify', 'Approval'];

  // Auto-search by user's email on mount
  useEffect(() => {
    if (user?.email) {
      setSearchQuery(user.email);
      handleAutoSearchByEmail();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.email]);

  const handleAutoSearchByEmail = async () => {
    if (!user?.email) return;

    setLoading(true);
    setErrorMessage('');

    try {
      console.log('🔍 ProfileClaiming: Auto-searching by email:', user.email);

      // Use ProfileLinkService for consistent search behavior
      const profiles = await profileLinkService.findMemberByEmail(user.email);

      setFoundProfiles(profiles);

      if (profiles.length === 1) {
        // Auto-select single matching profile
        setSelectedProfile(profiles[0]);
        console.log('✅ ProfileClaiming: Auto-selected single matching profile');
      } else if (profiles.length > 1) {
        console.log(`ℹ️ ProfileClaiming: Found ${profiles.length} matching profiles`);
      } else {
        console.log('ℹ️ ProfileClaiming: No profiles found by email');
      }
    } catch (error) {
      console.error('❌ ProfileClaiming: Error auto-searching by email:', error);
      // Don't show error for auto-search, just log it
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    setErrorMessage('');
    setErrors({});

    if (!searchQuery.trim()) {
      setErrors({ search: 'Please enter a name or member number' });
      return;
    }

    setLoading(true);

    try {
      // Use ProfileLinkService for consistent search behavior
      const searchTerm = searchQuery.trim();

      // Determine search type and build query
      const searchQuery_obj: any = {};

      // Check if it looks like a member number (numeric)
      if (/^\d+$/.test(searchTerm)) {
        searchQuery_obj.memberNumber = searchTerm;
      } else if (searchTerm.includes('@')) {
        // Email search
        searchQuery_obj.email = searchTerm;
      } else {
        // Name search
        searchQuery_obj.name = searchTerm;
      }

      const profiles = await profileLinkService.searchMemberProfiles(searchQuery_obj);

      if (profiles.length === 0) {
        setErrorMessage(
          'No unclaimed profiles found. Please try a different search or skip to create a new profile.',
        );
      }

      setFoundProfiles(profiles);
    } catch (error) {
      console.error('Search error:', error);
      setErrorMessage('Failed to search profiles. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectProfile = (profile: MemberProfile) => {
    setSelectedProfile(profile);
    setStep('verify');
  };

  const handleVerify = async () => {
    setErrorMessage('');
    setErrors({});

    if (!selectedProfile || !user) {
      setErrorMessage('Missing required information');
      return;
    }

    if (!memberNumber.trim()) {
      setErrors({ memberNumber: 'Member number is required' });
      return;
    }

    if (!verificationEmail.trim()) {
      setErrors({ email: 'Email is required' });
      return;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(verificationEmail)) {
      setErrors({ email: 'Please enter a valid email address' });
      return;
    }

    setLoading(true);

    try {
      console.log('✅ ProfileClaiming: Claiming profile:', selectedProfile.id);

      // Claim the profile directly (simplified flow for now)
      await authService.claimProfile(user.userId, selectedProfile.id);

      // Fetch the updated profile
      const updatedProfile = await authService.getMemberProfile(user.userId);

      if (updatedProfile) {
        // Update the auth context with the new profile
        updateProfile(updatedProfile);

        console.log('✅ ProfileClaiming: Profile claimed successfully');

        // Navigate to main app
        navigate('/chats', { replace: true });
      } else {
        throw new Error('Failed to load updated profile');
      }
    } catch (error) {
      console.error('❌ ProfileClaiming: Error claiming profile:', error);
      setErrorMessage('Failed to link profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = () => {
    // Mark that user has skipped profile claiming
    skipProfileClaim();
    // Navigate to main app with limited access
    navigate('/chats', { replace: true });
  };

  return (
    <Container
      maxWidth="md"
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        py: isMobile ? 2 : 4,
      }}
    >
      <Paper
        elevation={isMobile ? 0 : 3}
        sx={{
          p: isMobile ? 3 : 4,
          width: '100%',
          borderRadius: isMobile ? 0 : 2,
        }}
      >
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="h4" color="primary" gutterBottom fontWeight={600}>
            Claim Your Profile
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Link your account to your existing member profile
          </Typography>
        </Box>

        <Stepper
          activeStep={steps.indexOf(
            step === 'search' ? 'Find Profile' : step === 'verify' ? 'Verify' : 'Approval',
          )}
          sx={{ mb: 4 }}
        >
          {steps.map(label => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {errorMessage && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setErrorMessage('')}>
            {errorMessage}
          </Alert>
        )}

        {/* Step 1: Search for Profile */}
        {step === 'search' && (
          <Box>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Search for your existing member profile by name or member number.
            </Typography>

            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
              <TextField
                fullWidth
                label="Name or Member Number"
                value={searchQuery}
                onChange={e => {
                  setSearchQuery(e.target.value);
                  if (errors.search) {
                    const { search: _, ...rest } = errors;
                    setErrors(rest);
                  }
                }}
                error={!!errors.search}
                helperText={errors.search || ''}
                placeholder="e.g., John Smith or 12345"
                autoFocus
                disabled={loading}
              />
              <Button
                variant="contained"
                onClick={handleSearch}
                disabled={loading}
                sx={{ minWidth: 120 }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Search'}
              </Button>
            </Box>

            {foundProfiles.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Found Profiles
                </Typography>
                {foundProfiles.map(profile => (
                  <Card
                    key={profile.id}
                    sx={{
                      mb: 2,
                      cursor: 'pointer',
                      border: selectedProfile?.id === profile.id ? 2 : 1,
                      borderColor: selectedProfile?.id === profile.id ? 'primary.main' : 'divider',
                      '&:hover': { boxShadow: 3 },
                    }}
                    onClick={() => handleSelectProfile(profile)}
                  >
                    <CardContent>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'start',
                        }}
                      >
                        <Box>
                          <Typography variant="h6">{profile.displayName}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {profile.email}
                          </Typography>
                          {profile.initiationDate && (
                            <Typography variant="caption" color="text.secondary">
                              Initiated: {profile.initiationDate.toLocaleDateString()}
                            </Typography>
                          )}
                        </Box>
                        <Chip label={profile.status} color="primary" size="small" />
                      </Box>
                    </CardContent>
                  </Card>
                ))}

                {/* Quick claim button for selected profile */}
                {selectedProfile && (
                  <Box sx={{ mt: 2 }}>
                    <Button
                      fullWidth
                      variant="contained"
                      onClick={() => setStep('verify')}
                      size="large"
                    >
                      Continue with Selected Profile
                    </Button>
                  </Box>
                )}
              </Box>
            )}

            <Box sx={{ textAlign: 'center', mt: 4 }}>
              <Button variant="outlined" onClick={handleSkip} disabled={loading}>
                Skip for Now
              </Button>
              <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
                You can claim your profile later from settings
              </Typography>
            </Box>
          </Box>
        )}

        {/* Step 2: Verify Profile */}
        {step === 'verify' && selectedProfile && (
          <Box>
            <Alert severity="info" sx={{ mb: 3 }}>
              Please verify your identity to claim this profile
            </Alert>

            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {selectedProfile.displayName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {selectedProfile.email}
                </Typography>
                <Chip label={selectedProfile.status} color="primary" size="small" sx={{ mt: 1 }} />
              </CardContent>
            </Card>

            <TextField
              fullWidth
              label="Member Number"
              value={memberNumber}
              onChange={e => {
                setMemberNumber(e.target.value);
                if (errors.memberNumber) {
                  const { memberNumber, ...rest } = errors;
                  setErrors(rest);
                }
              }}
              error={!!errors.memberNumber}
              helperText={
                errors.memberNumber
                  ? errors.memberNumber
                  : 'Enter your member number for verification'
              }
              margin="normal"
              disabled={loading}
            />

            <TextField
              fullWidth
              label="Verification Email"
              type="email"
              value={verificationEmail}
              onChange={e => {
                setVerificationEmail(e.target.value);
                if (errors.email) {
                  const { email, ...rest } = errors;
                  setErrors(rest);
                }
              }}
              error={!!errors.email}
              helperText={
                errors.email ? errors.email : 'Enter the email associated with your member profile'
              }
              margin="normal"
              disabled={loading}
            />

            <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => {
                  setStep('search');
                  setSelectedProfile(null);
                }}
                disabled={loading}
              >
                Back
              </Button>
              <Button fullWidth variant="contained" onClick={handleVerify} disabled={loading}>
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Link Profile'}
              </Button>
            </Box>
          </Box>
        )}

        {/* Step 3: Pending Approval */}
        {step === 'pending' && (
          <Box sx={{ textAlign: 'center' }}>
            <Alert severity="success" sx={{ mb: 3 }}>
              Your profile claim request has been submitted!
            </Alert>

            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              An administrator will review your request and verify your information. You'll receive
              an email notification once your profile has been approved.
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
              In the meantime, you can continue using the portal with limited access.
            </Typography>

            <Button variant="contained" onClick={() => navigate('/')} size="large">
              Continue to Portal
            </Button>
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default ProfileClaiming;
